import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { styled } from '@mui/material'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { CellPropsBase, CellTitle, CellValueArea, FormCell } from '.'
import {
  SlackUser,
  extractSlackWorkspace,
} from '../../../../../domain/value-object/Slack/UserVO'
import { SlackWorkspace } from '../../../../../domain/value-object/Slack/WorkspaceVO'
import {
  getAuthorizedSlackWorkspaces,
  getSlackWorkspaceUsers,
} from '../../../../../infrastructure/api/slack'
import { addGlobalMessage, MessageLevel } from '../../../../../store/messages'
import { resolveWorkspaceSelection } from '../../hooks/slackUserSelection'
import { RangeDivider } from './RangeCellLayout'
import { SlackSelect } from './SlackSelect'

type SlackUserCellProps = {
  // 紐付け先ユーザー（この単票が表示しているエンティティ）のUUID。保存時に
  // どのユーザーのSlack連携かを表すため、選択値へ引き継ぐ必要がある。
  dataUuid: string
  value: SlackUser | undefined
  onChange?: (v: SlackUser | undefined) => void
} & CellPropsBase

export const SlackUserCell = ({
  dataUuid,
  value,
  onChange,
  cellWidth,
  label,
  help,
  editable = true,
  required,
  edited,
  validationErrors,
}: SlackUserCellProps) => {
  const [workspaceOptions, setWorkspaceOptions] = useState<SlackWorkspace[]>([])
  const [userOptions, setUserOptions] = useState<SlackUser[]>([])
  const intl = useIntl()
  const dispatch = useDispatch()

  // ワークスペース選択（uuid保持）の基準にする永続化済みの値。編集中は更新しない
  // ため、A→B→Aと切り替えてもAのuuidを復元できる。保存でeditedがfalseに戻る
  // たびに同期するため、保存後の切り替えでも保存した値のuuidを保てる。
  const baselineValueRef = useRef(value)
  useEffect(() => {
    if (!edited) {
      baselineValueRef.current = value
    }
  }, [edited, value])

  const handleFetchError = useCallback(
    (error: unknown, message: string) => {
      console.warn(message, error)
      // 非2xx応答は共通APIクライアントの経路ではスナックバー通知が出ないため、
      // Slack連携が動いていないことをユーザーに知らせるためここで出す。
      dispatch(
        addGlobalMessage({
          type: MessageLevel.WARN,
          title: intl.formatMessage({ id: 'slack.fetchError' }),
        })
      )
    },
    [dispatch, intl]
  )

  // 選択肢は編集時にしか使わない（表示は value から描画される）ため、読み取り専用
  // なら取得しない。未連携テナントでは空配列のままになり、選択肢なしとして描画される。
  useEffect(() => {
    if (!editable) return
    let isCancelled = false
    getAuthorizedSlackWorkspaces()
      .then(workspaces => {
        if (isCancelled || !workspaces) return
        setWorkspaceOptions(workspaces)
      })
      .catch(error => {
        if (isCancelled) return
        handleFetchError(error, 'Failed to fetch authorized Slack workspaces.')
      })
    return () => {
      isCancelled = true
    }
  }, [editable, handleFetchError])

  // ワークスペースを続けて切り替えたとき、先に投げた取得が後から解決して
  // 別ワークスペースのユーザー一覧を表示してしまわないよう、古い応答は破棄する。
  useEffect(() => {
    // ワークスペースを切り替えた直後は、取得が終わるまで候補を空にする。
    // ユーザー欄はワークスペースが決まった時点で有効になるため、前のワークスペースの
    // 候補を残したままだと、そこから選んで「新しいワークスペースID＋古いユーザーID」
    // という実在しない組み合わせを作れてしまう。
    setUserOptions([])
    if (!editable || !value?.slackWorkspaceId) {
      return
    }
    let isCancelled = false
    getSlackWorkspaceUsers(value.slackWorkspaceId)
      .then(users => {
        if (isCancelled || !users) return
        setUserOptions(users)
      })
      .catch(error => {
        if (isCancelled) return
        handleFetchError(error, 'Failed to fetch Slack workspace users.')
        setUserOptions([])
      })
    return () => {
      isCancelled = true
    }
  }, [editable, handleFetchError, value?.slackWorkspaceId])

  const selectedWorkspace = useMemo(
    () => (value ? extractSlackWorkspace(value) : null),
    [value]
  )

  const handleChangeWorkspace = useCallback(
    (_: unknown, workspace: SlackWorkspace | null) => {
      if (!workspace) {
        onChange && onChange(undefined)
        return
      }
      onChange &&
        onChange(resolveWorkspaceSelection(baselineValueRef.current, workspace))
    },
    [onChange]
  )

  const handleChangeUser = useCallback(
    (_: unknown, user: SlackUser | null) => {
      if (!user) {
        // ユーザー欄のクリアボタンで消えるのはユーザーだけにし、ワークスペースの
        // 選択はincomplete状態として残すため、選び直さずに別のユーザーを選べる。
        onChange &&
          onChange(
            value && {
              ...value,
              slackUserId: undefined,
              slackUserName: undefined,
            }
          )
        return
      }
      const nextValue: SlackUser = {
        ...user,
        // 既存レコードの識別子（uuid）と紐付け先ユーザー（userUuid）は選択肢側の
        // データに含まれないため、選択中の値と単票のエンティティから引き継ぐ。
        uuid: value?.uuid,
        userUuid: dataUuid || undefined,
        slackCollaborationWorkspaceUuid: value?.slackCollaborationWorkspaceUuid,
        slackWorkspaceId: value?.slackWorkspaceId,
        slackWorkspaceName: value?.slackWorkspaceName,
      }
      onChange && onChange(nextValue)
    },
    [dataUuid, onChange, value]
  )

  return (
    <FormCell cellWidth={cellWidth}>
      <CellTitle title={label} required={required} help={help} />
      <CellValueArea edited={edited} validationErros={validationErrors}>
        <RootDiv>
          <SlackSelect
            options={workspaceOptions}
            value={selectedWorkspace}
            onChange={handleChangeWorkspace}
            getOptionLabel={option => option.slackWorkspaceName ?? ''}
            isOptionEqualToValue={(option, v) =>
              option.slackWorkspaceId === v.slackWorkspaceId
            }
            disabled={!editable}
          />
          <RangeDivider />
          <SlackSelect
            options={userOptions}
            // ワークスペースのみ選択済みの中間状態では、ユーザーは未選択として
            // 空欄を表示する。
            value={value?.slackUserId ? value : null}
            onChange={handleChangeUser}
            getOptionLabel={option => option.slackUserName ?? ''}
            isOptionEqualToValue={(option, v) =>
              option.slackUserId === v.slackUserId
            }
            disabled={!editable || !value?.slackWorkspaceId}
          />
        </RootDiv>
      </CellValueArea>
    </FormCell>
  )
}

const RootDiv = styled('div')({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
})
